import React, { useState } from 'react';
import { ClipboardPaste, Plus } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import { Textarea } from '@dashboard/components/ui/textarea';
import { parseBulk } from '../lib/parseBulkInvites';
import SectionDisclosure from './SectionDisclosure';
import TicketTypeRestriction, { type TicketTypeOption } from './TicketTypeRestriction';

interface Props {
  ticketTypeOptions: TicketTypeOption[];
  onAdd: (entries: Array<{ email: string; name?: string; allowedTicketTypes: string[] }>) => void;
  disabled?: boolean;
}

const PasteEmailsDisclosure: React.FC<Props> = ({ ticketTypeOptions, onAdd, disabled }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [allowedTicketTypes, setAllowedTicketTypes] = useState<string[]>([]);

  const parsed = parseBulk(value);
  const valid = parsed.filter(p => !p.invalid);
  const invalid = parsed.filter(p => p.invalid);

  const handleAdd = () => {
    if (valid.length === 0) return;
    onAdd(valid.map(p => ({ email: p.email, name: p.name, allowedTicketTypes })));
    setValue('');
    setOpen(false);
  };

  return (
    <SectionDisclosure
      icon={ClipboardPaste}
      label="Paste a list of emails"
      open={open}
      onOpenChange={setOpen}
    >
      <p className="text-xs text-muted-foreground">
        One invite per line. Optionally include a name.
      </p>
      <Textarea
        rows={4}
        placeholder={`john@example.com    John Doe\njane@example.com, Jane Doe`}
        value={value}
        onChange={e => setValue(e.target.value)}
        disabled={disabled}
        className="font-mono text-sm"
      />
      {invalid.length > 0 && (
        <p className="text-xs text-destructive">
          {invalid.length === 1
            ? `Line ${invalid[0].lineNumber}: "${invalid[0].invalid}" doesn't look like an email address.`
            : `${invalid.length} lines don't look like email addresses.`}
        </p>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Ticket scanning:</span>
          <TicketTypeRestriction
            options={ticketTypeOptions}
            value={allowedTicketTypes}
            onChange={setAllowedTicketTypes}
            disabled={disabled}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled || valid.length === 0 || invalid.length > 0}
        >
          <Plus className="size-3.5 mr-1" />
          Add to staff
        </Button>
      </div>
    </SectionDisclosure>
  );
};

export default PasteEmailsDisclosure;
